import type { BorderSettings } from './index';
import type { CssVars } from '../../css-vars';
import type { Device, Side } from '../../utils';
import { DEVICE_SUFFIX, SIDES, atDevice } from '../../utils';

/**
 * `<width>px <style> <color>` for one side. Plain values rather than the
 * feature's shape, so a widget that still stores its sides as flat legacy keys
 * formats them the same way.
 */
export function borderSideValueVar(
    side: { width?: number | null; style?: string | null; color?: string | null } | null | undefined
): string {
    return `${side?.width ?? 0}px ${side?.style ?? ''} ${side?.color ?? ''}`;
}

type LegacyBorderSideKey = '' | 'Top' | 'Right' | 'Bottom' | 'Left';

/** The flat `border<Side>Width/Style/Color` keys the widgets still store. */
export type LegacyBorderBag = Partial<
    Record<`border${LegacyBorderSideKey}${'Width'}`, number | null> &
        Record<`border${LegacyBorderSideKey}${'Style' | 'Color'}`, string | null>
>;

const LEGACY_SIDE: Record<Side, LegacyBorderSideKey> = { top: 'Top', right: 'Right', bottom: 'Bottom', left: 'Left' };

/**
 * One side read off a legacy flat bag — `borderTopWidth` and friends — or, with
 * no side, the `borderWidth` / `borderStyle` / `borderColor` shorthand trio.
 */
export function legacyBorderSideVar(bag: LegacyBorderBag | null | undefined, side?: Side): string {
    const key = side ? LEGACY_SIDE[side] : '';

    return borderSideValueVar({
        width: bag?.[`border${key}Width`],
        style: bag?.[`border${key}Style`],
        color: bag?.[`border${key}Color`],
    });
}

/**
 * A whole `border` shorthand per side, so the "no contour" case is just `none` —
 * the JSS had to emit `border: none !important` on every device twin instead.
 */
export function borderSideVar(border: BorderSettings | undefined, side: Side, device: Device = 'desktop'): string {
    if (!atDevice(border?.isEnabled, device)) {
        return 'none';
    }

    return borderSideValueVar(atDevice(border?.[side], device));
}

/** `--<prefix>-border-<side>[-tablet|-mobile]` for the four sides. */
export function borderVars(border: BorderSettings | undefined, prefix: string, device: Device = 'desktop'): CssVars {
    return Object.fromEntries(
        SIDES.map((side) => [`--${prefix}-border-${side}${DEVICE_SUFFIX[device]}`, borderSideVar(border, side, device)])
    );
}

/** How wide one side is drawn — `0px` while the contour is off — for a box that has to leave room for it. */
export function borderWidthVar(border: BorderSettings | undefined, side: Side, device: Device = 'desktop'): string {
    if (!atDevice(border?.isEnabled, device)) {
        return '0px';
    }

    return `${atDevice(border?.[side], device)?.width ?? 0}px`;
}

/** `--<prefix>-border-<side>-width[-tablet|-mobile]` for the four sides. */
export function borderWidthVars(
    border: BorderSettings | undefined,
    prefix: string,
    device: Device = 'desktop'
): CssVars {
    return Object.fromEntries(
        SIDES.map((side) => [
            `--${prefix}-border-${side}-width${DEVICE_SUFFIX[device]}`,
            borderWidthVar(border, side, device),
        ])
    );
}
